import { ClockIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getPermissionDetails, updatePermissionDescription, updatePermissions } from 'services/apis/admin'
import { svgLoading } from 'stories/assets'
import { Button, Checkbox, Input2, Tooltip } from 'stories/components'
import { openAuditLog } from 'utils'

export function NewPermissions() {
  const [isLoading, setIsLoading] = useState(false)
  const [permissions, setPermissions] = useState<any>({})
  const [roles, setRoles] = useState<Record<number, string>>({})
  const [permissionRoles, setPermissionRoles] = useState<Record<number, number[]>>({})
  const [permissionDescriptions, setPermissionDescriptions] = useState<Record<string, string>>({})
  const [permissionTooltips, setPermissionTooltips] = useState<Record<string, string>>({})
  const [permissionIds, setPermissionIds] = useState<Record<string, number>>({})
  const [oldNames, setOldNames] = useState<Record<string, string>>({})
  const [orders, setOrders] = useState<number[]>([])
  const [editID, setEditID] = useState(-1)
  const [editRoles, setEditRoles] = useState<any>([])
  const [action, setAction] = useState('')
  const [search, setSearch] = useState('')
  const [editDescPermission, setEditDescPermission] = useState<string | null>(null)
  const [editDescValue, setEditDescValue] = useState<{ description: string; tooltip: string }>({
    description: '',
    tooltip: '',
  })
  const [isSavingDesc, setIsSavingDesc] = useState(false)

  const initData = async () => {
    setIsLoading(true)
    const res = await getPermissionDetails()
    setIsLoading(false)
    if (res.success) {
      setPermissions(res.data.permissions)
      setRoles(res.data.roles)
      setPermissionRoles(res.data.permissionRoles)
      setPermissionDescriptions(res.data.permissionDescriptions)
      setPermissionTooltips(res.data.permissionTooltips || {})
      setPermissionIds(res.data.permissionIds || {})
      setOldNames(res.data.oldMapping)
      setOrders(res.data.orders)
    }
  }

  const mapRoles = useMemo(() => {
    let rlt: Record<string, number> = {}
    Object.keys(roles).map((id) => {
      rlt[roles[Number(id)]] = Number(id)
    })
    return rlt
  }, [roles])

  useEffect(() => {
    initData()
  }, [])

  const onChangePermission = (key: string, value: boolean) => {
    const roleID = mapRoles[key]
    let temp = cloneDeep(editRoles)
    if (value) {
      temp.push(roleID)
    } else {
      const index = editRoles.findIndex((id: number) => id === roleID)
      temp.splice(index, 1)
    }
    setEditRoles(temp)
  }

  const onSaveChanges = async () => {
    setIsLoading(true)
    setAction('save')
    const json = {
      permissionID: editID,
      roleIDs: editRoles,
    }
    await updatePermissions(json)
    let temp = cloneDeep(permissionRoles)
    temp[editID] = editRoles
    setPermissionRoles(temp)
    setIsLoading(false)
    setEditID(-1)
    setAction('')
  }

  const onSaveCancel = () => {
    setEditID(-1)
  }

  const onEditPermissionDesc = (permissionName: string) => {
    setEditDescPermission(permissionName)
    setEditDescValue({
      description: permissionDescriptions[permissionName] || '',
      tooltip: permissionTooltips[permissionName] || '',
    })
  }

  const onCancelPermissionDesc = () => {
    setEditDescPermission(null)
    setEditDescValue({ description: '', tooltip: '' })
  }

  const onSavePermissionDesc = async () => {
    if (!editDescPermission) return
    const id = permissionIds[editDescPermission]
    if (id == null) return
    setIsSavingDesc(true)
    await updatePermissionDescription(id, editDescValue.description, editDescValue.tooltip)
    setPermissionDescriptions((prev) => {
      const next = cloneDeep(prev)
      next[editDescPermission] = editDescValue.description
      return next
    })
    setPermissionTooltips((prev) => {
      const next = cloneDeep(prev)
      next[editDescPermission] = editDescValue.tooltip
      return next
    })
    setIsSavingDesc(false)
    setEditDescPermission(null)
    setEditDescValue({ description: '', tooltip: '' })
  }

  const onSelectAll = () => {
    const allRoleIDs = Object.values(mapRoles)
    setEditRoles(allRoleIDs)
  }

  const renderAccountTypes = (permissionID: string) => {
    const data: any = permissionRoles[Number(permissionID)]
    if (!data) return null
    let accounts: any = []
    Object.keys(accountTypes).map((key) => {
      if (data.includes(mapRoles[key])) accounts.push(accountTypes[key])
    })
    if (Number(permissionID) === editID) {
      return (
        <div className="mb-6 bg-stone-100 p-4 rounded">
          <div className="flex flex-wrap gap-2">
            {Object.keys(accountTypes).map((key) => {
              const selected = editRoles.includes(mapRoles[key])
              return (
                <div className="w-[175px]" key={key}>
                  <Checkbox
                    id={key}
                    title={accountTypes[key]}
                    key={key}
                    value={selected}
                    onChange={(value) => onChangePermission(key, value)}
                  />
                </div>
              )
            })}
          </div>
          <div className="flex flex-wrap gap-6 justify-center mt-4">
            <Button link onClick={onSelectAll} className="py-[3px]">
              <span className="text-primary">Select All</span>
            </Button>
            <Button onClick={onSaveChanges} className="py-[3px]" loading={action === 'save'}>
              Save Changes
            </Button>
            <Button link onClick={onSaveCancel} className="py-[3px]">
              <span className="text-red-500">Cancel</span>
            </Button>
          </div>
        </div>
      )
    }
    return (
      <div className="flex flex-wrap gap-2">
        {accounts.map((item: string, index: number) => {
          return (
            <div className="italic border p-1 rounded w-[175px] text-center text-[13.5px]" key={index}>
              {item}
            </div>
          )
        })}
      </div>
    )
  }

  const editPermission = (permissionID: string) => {
    if (editID !== -1) {
      return toast('You are editing the permission!', { type: 'error' })
    }
    setEditID(Number(permissionID))
    setEditRoles(permissionRoles[Number(permissionID)])
  }

  const showHistory = (key: any) => {
    const name = permissions[key]
    const options = {
      table: 'Permission',
      field: name,
      keys: {
        field: name,
      },
    }
    openAuditLog(options)
  }

  const orderedPermissions = useMemo(() => {
    const data: Record<string, number> = {}
    Object.keys(permissions).forEach((id, index) => {
      const permissionName = permissions[id]
      data[permissionName] = orders[index]
    })
    return Object.keys(data).sort((a, b) => data[a] - data[b])
  }, [orders, permissions])

  const groupedPermissions = useMemo(() => {
    const groups: { category: string; items: string[] }[] = []
    const groupIndexByCategory: Record<string, number> = {}

    orderedPermissions.forEach((permissionName) => {
      const dotIndex = permissionName.indexOf('.')
      const category = dotIndex > -1 ? permissionName.substring(0, dotIndex) : permissionName

      if (groupIndexByCategory[category] === undefined) {
        groupIndexByCategory[category] = groups.length
        groups.push({ category, items: [] })
      }
      groups[groupIndexByCategory[category]].items.push(permissionName)
    })

    return groups
  }, [orderedPermissions])

  const filteredGroupedPermissions = useMemo(() => {
    if (!search.trim()) return groupedPermissions
    const query = search.toLowerCase()
    return groupedPermissions
      .map((group) => {
        const items = group.items.filter((permissionName) => {
          const key = Object.keys(permissions).find((id) => permissions[id] === permissionName)
          if (!key) return false
          const name = permissions[key]
          const displayName = name.startsWith(group.category + '.') ? name.substring(group.category.length + 1) : name
          if (name.toLowerCase().includes(query)) return true
          if (displayName.toLowerCase().includes(query)) return true
          if ((oldNames[name] || '').toLowerCase().includes(query)) return true
          if ((permissionDescriptions[name] || '').toLowerCase().includes(query)) return true
          if ((permissionTooltips[name] || '').toLowerCase().includes(query)) return true
          return false
        })
        return { ...group, items }
      })
      .filter((group) => group.items.length > 0)
  }, [groupedPermissions, search, permissions, oldNames, permissionDescriptions, permissionTooltips])

  return (
    <div className="newPermissions-container relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl font-bold">
        Manage Permissions
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <div className="mt-3 max-w-sm">
        <Input2
          type="text"
          name="permission-search"
          title="Search permissions..."
          value={search}
          onChange={(value: string) => setSearch(value)}
        />
      </div>
      <div className="mt-4">
        {filteredGroupedPermissions.map((group, groupIndex) => (
          <div key={group.category} className="mb-6">
            <p className="border-b-2 border-shade-blue w-full italic">
              {String.fromCharCode(65 + groupIndex)}. {group.category}
            </p>

            {group.items.map((permissionName, itemIndex) => {
              const key = Object.keys(permissions).find((id) => permissions[id] === permissionName)
              if (!key) return null

              const name = permissions[key]
              const displayName = name.startsWith(group.category + '.')
                ? name.substring(group.category.length + 1)
                : name
              const tooltipText = permissionTooltips[name] || ''

              const isEditingDesc = editDescPermission === permissionName

              return (
                <div className="mb-5 pl-4" key={key}>
                  <div className="group flex flex-wrap items-center gap-2 border-b">
                    <div
                      className="flex gap-2 items-center hover:text-shade-blue cursor-pointer"
                      onClick={() => editPermission(key)}
                    >
                      <div className="text-[14px] font-semibold">
                        {tooltipText ? (
                          <Tooltip message={tooltipText}>
                            <span>
                              {itemIndex + 1}. {displayName}
                            </span>
                          </Tooltip>
                        ) : (
                          <span>
                            {itemIndex + 1}. {displayName}
                          </span>
                        )}
                      </div>
                      <div className="hidden group-hover:inline">
                        <Tooltip message="Update Permission">
                          <PencilSquareIcon className="w-4 h-4" />
                        </Tooltip>
                      </div>
                    </div>
                    <div
                      className="hidden group-hover:inline hover:text-shade-blue cursor-pointer"
                      onClick={() => showHistory(key)}
                    >
                      <ClockIcon className="w-4 h-4" />
                    </div>

                    <div className="flex-1" />

                    <div className="text-xs text-gray-500">{oldNames[name] || ''}</div>
                    <div className="hidden group-hover:inline">
                      <Tooltip message="Edit Description">
                        <button
                          className="hover:text-shade-blue cursor-pointer"
                          onClick={() => onEditPermissionDesc(permissionName)}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>

                  {isEditingDesc && (
                    <div className="mt-2 px-2 pb-2 bg-stone-50 rounded">
                      <div className="flex gap-3 pt-2">
                        <div className="flex-1">
                          <Input2
                            title="Description"
                            value={editDescValue.description}
                            onChange={(v: string) =>
                              setEditDescValue((prev) => {
                                const next = cloneDeep(prev)
                                next.description = v
                                return next
                              })
                            }
                          />
                        </div>
                        <div className="flex-1">
                          <Input2
                            title="Tooltip"
                            value={editDescValue.tooltip}
                            onChange={(v: string) =>
                              setEditDescValue((prev) => {
                                const next = cloneDeep(prev)
                                next.tooltip = v
                                return next
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="flex gap-4 mt-2 justify-end">
                        <Button onClick={onSavePermissionDesc} className="py-[3px]" loading={isSavingDesc}>
                          Save
                        </Button>
                        <Button link onClick={onCancelPermissionDesc} className="py-[3px]">
                          <span className="text-red-500">Cancel</span>
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="mt-2 px-2">{renderAccountTypes(key)}</div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
